/**
 * The geometry half of QA-6: `component.*.width` -> `grid-template-columns`.
 *
 * `docs/QA-PARITY.md` QA-6. The three deployed configs ask for a 300px row label plus five 200px
 * day cells; with the `gap-2` between them that is a 1348px row laid out inside the 3/4 column of
 * a **1080px** screen. 571px of it hung off the right-hand edge, and since nothing scrolls a
 * signage display, **Wednesday, Thursday and Friday were not on the wall** — in this app and,
 * identically, in the legacy one, on three displays, for as long as they have been up.
 *
 * The widths are config data and config data is immovable (CLAUDE.md constraint 1), so this stops
 * reading them as lengths and starts reading them as **proportions**: the row label gets one
 * track and the days get `columnCount` identical ones, each `minmax(0, <configured>fr)`. The
 * configured 300:200:200:… ratio survives exactly, and the row as a whole is always precisely as
 * wide as the space it has — narrower than configured at 1080, wider at 2160, overflowing at
 * neither. Not one byte of any config changes and no new config key is involved.
 *
 * **`minmax(0, …fr)` and never a bare `…fr`.** An `fr` track's automatic minimum is min-content,
 * so a long row label or a wide cell value would push its track past its share and overflow the
 * row — the exact defect being fixed, reintroduced one level down. The other half of that
 * guarantee is `.bookings-grid > .label` in `global.css`, which clips the text instead of letting
 * it push: **the cell wins.** Losing the tail of a word is a much smaller loss than losing three
 * days of the week.
 *
 * Its own module rather than a second export from `BookingsWidget.tsx`, so the component file
 * exports a component and this is unit-testable on its own — which matters here because jsdom
 * computes no grid layout, making this string the only part of the fix a unit test can see.
 */

/** The three label blocks, as far as track sizing is concerned. */
export type TrackWidths = {
  rowLabel?: { width?: number | string };
  columnLabel?: { width?: number | string };
  cellLabel?: { width?: number | string };
};

/**
 * The legacy `LABEL_DEFAULTS.width` of `'300px'`, as a ratio. All three label blocks share it,
 * so a config that configures no widths at all gets equal columns.
 */
export const DEFAULT_WIDTH_RATIO = 300;

export function trackTemplate(component: TrackWidths, columnCount: number): string {
  const rowTrack = widthRatio(component.rowLabel?.width);
  // `columnLabel.width` and `cellLabel.width` describe the same column, so one of them has to
  // win: the header does, because a column header *is* the column. A config that sizes only the
  // cells still gets the ratio it asked for. Both are `200px` in all three deployed configs.
  const dayTrack = widthRatio(component.columnLabel?.width ?? component.cellLabel?.width);

  const template = `minmax(0, ${String(rowTrack)}fr)`;
  if (columnCount <= 0) return template;
  return `${template} repeat(${String(columnCount)}, minmax(0, ${String(dayTrack)}fr))`;
}

/**
 * The numeric part of a configured width, as an `fr` ratio.
 *
 * Deliberately unit-blind — `"200px"`, `"200"` and `200` are one ratio — because the unit stops
 * meaning anything the moment a width becomes a proportion. That also makes mixing units across
 * the three blocks a config mistake rather than a supported case; all three deployed configs use
 * `px` throughout.
 *
 * A missing, non-numeric or non-positive value falls back to the legacy default, so a
 * hand-edited config degrades to equal columns rather than to a collapsed table (CLAUDE.md
 * constraint 4).
 */
function widthRatio(width: number | string | undefined): number {
  const value = typeof width === 'string' ? Number.parseFloat(width) : width;
  if (value === undefined || !Number.isFinite(value) || value <= 0) return DEFAULT_WIDTH_RATIO;
  return value;
}
